/**
 * Reading an untyped interrupt payload — whatever the model passed as tool arguments, so every mapper in
 * this folder reads defensively rather than casting. These are the primitives they all need, in one
 * place: the display mappers, the reducer and the interrupt component each grew their own copy, and
 * three of them pretty-printed their JSON while one did not.
 *
 * The two stringifiers are named for their audience rather than their options: one output is read
 * by a maker, the other is squeezed into a trace row.
 */

export function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

export function asString(value: unknown): string {
  if (typeof value === 'string') {
    return value
  }

  return typeof value === 'number' || typeof value === 'boolean' ? String(value) : ''
}

/** Read non-blank text without scalar coercion or changing the supplied whitespace. */
export function asNonBlankString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

export function firstNonEmpty(...values: string[]): string {
  return values.find(Boolean) ?? ''
}

export function asStringList(value: unknown): string[] {
  return asArray(value)
    .map((entry) => asString(entry))
    .filter(Boolean)
}

export function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

export function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

/** Absent stays absent: a count defaulted to 0 would read as "nothing is described here", which
 * is a claim about the application rather than about what was sent. */
export function asOptionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

export function asStringMap(value: unknown): Record<string, string> {
  return Object.fromEntries(
    Object.entries(isRecord(value) ? value : {})
      .map(([key, entry]) => [key, asString(entry)] as const)
      .filter(([, entry]) => !!entry)
  )
}

/** Unlike empty strings in asStringMap, zero is a meaningful count and must be kept. */
export function asNumberMap(value: unknown): Record<string, number> {
  return Object.fromEntries(
    Object.entries(isRecord(value) ? value : {}).filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number'
    )
  )
}

/** Parse JSON when possible; preserve non-JSON text verbatim for display. */
export function tryParseJson(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

/** Attach the original payload when a mapper found no structured content worth rendering. */
export function withRawFallback<T extends { raw?: string }>(
  value: T,
  isEmpty: boolean,
  payload: unknown
): T {
  return isEmpty ? { ...value, raw: prettyJson(payload) } : value
}

/** For a rendered fallback — a payload a maker will read, so it is indented. */
export function prettyJson(value: unknown): string {
  return stringify(value, 2)
}

/** For a one-line summary — a trace row, a notice — where newlines cost more than they say. */
export function compactJson(value: unknown): string {
  return stringify(value)
}

function stringify(value: unknown, indent?: number): string {
  try {
    return JSON.stringify(value, null, indent) ?? String(value)
  } catch {
    return String(value)
  }
}
